import { createContext, Dispatch, useContext, useMemo } from 'react';
import { AnyAction } from 'redux';

import { STRATEGIES } from '#/shared/strategies';
import { GrfqVenue } from '#/unified-rfqs/entities/products/venue';
import { Counterparty } from '#/unified-rfqs/entities/drfq/domain';

import { useDefaultAccountsView } from '#/preferences/default-accounts/context';
import { AuctionType } from '#/unified-rfqs/entities/shared';
import { useMultiProductVenues } from '#/feature-flags/context';
import { MULTI_PRODUCT_OPTION_VALUE } from './multi-product';
import {
  venueSelection,
  productSelection,
  multiProductSelection,
  auctionSelection,
  strategySelection,
  futureLegExpirationSelection,
  optionLegExpirationSelection,
  optionLegStrikeSelection,
  legQuantitySelection,
  legProductSelection,
  legActionToggle,
  optionLegInstrumentSelection,
  legRatioChange,
  legPriceChange,
  clearAllLegsRequest,
  newLegRequest,
  legRemovalRequest,
  addHedgeLeg,
  counterpartiesSelection,
  toggleAnonymous,
  duplicateLeg as duplicateLegAction,
  accountSelection,
  hiddenLimitOrderToggle,
  hiddenLimitOrderBidPriceChange,
  hiddenLimitOrderAskPriceChange,
} from './store';

import { OptionKind } from './types';
import { View } from './view';

interface SuccessReaction {
  readonly ok: true;
}

interface FailureReaction {
  readonly ok: false;
  readonly message: string;
}

type Reaction = SuccessReaction | FailureReaction;

export interface Actions {
  /**
   * Updates the selected venue and resets the rest of the dependent state
   */
  readonly selectVenue: (venue: GrfqVenue['code'] | string | null) => void;
  /**
   * Updates the selected product and resets the rest of the dependent state
   */
  readonly selectProduct: (productCode: string | null) => void;

  /**
   * Updates the selected account
   */
  readonly selectAccount: (account: string | null) => void;

  /**
   * Updates the selected auction
   */
  readonly selectAuction: (auction: AuctionType | null) => void;

  /**
   * Updates the selected strategy and replace all legs with legs with
   * brand new legs from the strategy.
   */
  readonly selectStrategy: (strategyCode: string | null) => void;

  /**
   * Updates the expiration of a future leg.
   * Also looks into the strategy and update dependent legs.
   */
  readonly selectFutureLegExpiration: (
    legIndex: number,
    expiration: number | null,
  ) => void;

  /**
   * Updates the expiration of an option leg.
   * Also looks into the strategy and update dependent legs.
   */
  readonly selectOptionLegExpiration: (
    legIndex: number,
    expiration: number,
  ) => void;

  /**
   * Updates the strike of an option leg.
   * Also looks into the strategy and update dependent legs.
   */
  readonly selectOptionLegStrike: (legIndex: number, strike: string) => void;

  /**
   * Updates a leg quantity
   */
  readonly selectLegQuantity: (legIndex: number, quantity: string) => void;

  /**
   * Updates a leg product
   */
  readonly selectLegProduct: (legIndex: number, productCode: string) => void;

  /**
   * Toggle between BUY and SELL actions
   */
  readonly toggleLegAction: (
    legIndex: number,
    strategyCode: string | null,
  ) => Reaction;

  /**
   * Updates instrument (optionKind) of an option leg
   */
  readonly selectOptionLegInstrument: (
    legIndex: number,
    optionKind: OptionKind,
  ) => void;

  readonly changeLegRatio: (legIndex: number, ratio: string) => void;

  readonly changeLegPrice: (legIndex: number, price: string | null) => void;

  readonly clearAllLegs: () => void;

  /**
   * Adds a new empty leg to the end of the legs list
   */
  readonly addNewLeg: () => void;

  readonly removeLeg: (legIndex: number) => void;

  readonly addNewHedgeLeg: (hedgeView: View['hedge']) => void;

  readonly counterpartiesSelection: (cps: readonly Counterparty[]) => void;

  readonly toggleAnonymous: () => void;

  readonly duplicateLeg: (legIndex: number) => void;

  readonly hiddenLimitOrderToggle: (enabled: boolean) => void;

  readonly hiddenLimitOrderBidPriceChange: (price: string) => void;

  readonly hiddenLimitOrderAskPriceChange: (price: string) => void;
}

function throwMissingProvider(): never {
  throw new Error(
    'Use ActionsCtx.Provider to provide an implementation for the ActionsCtx functions.',
  );
}

const DEFAULT_VALUE: Actions = {
  selectVenue: throwMissingProvider,
  selectProduct: throwMissingProvider,
  selectAccount: throwMissingProvider,
  selectAuction: throwMissingProvider,
  selectStrategy: throwMissingProvider,
  selectFutureLegExpiration: throwMissingProvider,
  selectOptionLegExpiration: throwMissingProvider,
  selectOptionLegStrike: throwMissingProvider,
  selectLegQuantity: throwMissingProvider,
  selectLegProduct: throwMissingProvider,
  toggleLegAction: throwMissingProvider,
  selectOptionLegInstrument: throwMissingProvider,
  changeLegRatio: throwMissingProvider,
  changeLegPrice: throwMissingProvider,
  clearAllLegs: throwMissingProvider,
  addNewLeg: throwMissingProvider,
  removeLeg: throwMissingProvider,
  addNewHedgeLeg: throwMissingProvider,
  counterpartiesSelection: throwMissingProvider,
  toggleAnonymous: throwMissingProvider,
  duplicateLeg: throwMissingProvider,
  hiddenLimitOrderToggle: throwMissingProvider,
  hiddenLimitOrderBidPriceChange: throwMissingProvider,
  hiddenLimitOrderAskPriceChange: throwMissingProvider,
};

export const ActionsCtx = createContext<Actions>(DEFAULT_VALUE);

ActionsCtx.displayName = 'RfqBuilderActions';

export function useRfqBuilderActions() {
  return useContext(ActionsCtx);
}

export function useCreateActions(dispatch: Dispatch<AnyAction>): Actions {
  const defaultAccounts = useDefaultAccountsView();
  const multiProductVenues = useMultiProductVenues();

  return useMemo(
    () => ({
      selectVenue: (venue) => {
        dispatch(
          venueSelection({
            defaultAccounts,
            venue: venue as GrfqVenue['code'],
          }),
        );
      },

      toggleAnonymous: () => {
        dispatch(toggleAnonymous());
      },

      selectProduct: (productCode: string | null) => {
        // "Multi-Product" is a mode, not a product: it seeds a custom
        // structure with empty legs instead of selecting a product.
        if (productCode === MULTI_PRODUCT_OPTION_VALUE) {
          dispatch(multiProductSelection());
          return;
        }
        dispatch(productSelection({ productCode, defaultAccounts }));
      },

      selectAccount: (account: string | null) => {
        dispatch(accountSelection(account));
      },

      selectAuction: (auction: AuctionType | null) => {
        dispatch(auctionSelection(auction));
      },

      selectStrategy: (strategyCode: string | null) => {
        dispatch(strategySelection(strategyCode));
      },

      selectFutureLegExpiration: (
        legIndex: number,
        expiration: number | null,
      ) => {
        dispatch(futureLegExpirationSelection({ expiration, legIndex }));
      },

      selectOptionLegExpiration: (legIndex: number, expiration: number) => {
        dispatch(optionLegExpirationSelection({ expiration, legIndex }));
      },

      selectOptionLegStrike: (legIndex: number, strike: string) => {
        dispatch(optionLegStrikeSelection({ strike, legIndex }));
      },

      selectLegQuantity: (legIndex: number, quantity: string) => {
        dispatch(
          legQuantitySelection({
            quantity,
            legIndex,
          }),
        );
      },

      selectLegProduct: (legIndex: number, productCode: string) => {
        dispatch(
          legProductSelection({
            productCode,
            legIndex,
            defaultAccounts,
            multiProductVenues,
          }),
        );
      },

      toggleLegAction: (legIndex: number, strategyCode: string | null) => {
        if (strategyCode === null) {
          dispatch(legActionToggle({ legIndex }));
          return { ok: true };
        }

        const strategy = STRATEGIES[strategyCode];
        if (strategy === undefined)
          throw new TypeError(
            `Strategy with code ${strategyCode} does not exist`,
          );

        if (strategy.legs.length === 1 && legIndex === 0)
          return {
            ok: false,
            message:
              'Please submit single-leg RFQs as a BUY.' +
              ' You will receive two-way quotes after submitting.',
          };

        dispatch(legActionToggle({ legIndex }));
        return { ok: true };
      },

      selectOptionLegInstrument: (legIndex: number, optionKind: OptionKind) => {
        dispatch(optionLegInstrumentSelection({ legIndex, optionKind }));
      },

      changeLegRatio: (legIndex: number, ratio: string) => {
        dispatch(legRatioChange({ legIndex, ratio }));
      },

      changeLegPrice: (legIndex: number, price: string | null) => {
        dispatch(
          legPriceChange({
            legIndex,
            price,
          }),
        );
      },

      duplicateLeg: (legIndex: number) => {
        dispatch(duplicateLegAction({ legIndex }));
      },

      clearAllLegs: () => {
        dispatch(clearAllLegsRequest());
      },

      addNewLeg: () => {
        dispatch(newLegRequest());
      },

      removeLeg: (legIndex: number) => {
        dispatch(legRemovalRequest({ legIndex }));
      },

      addNewHedgeLeg: (hedge: View['hedge']) => {
        dispatch(addHedgeLeg(hedge));
      },

      counterpartiesSelection: (cps) => {
        dispatch(counterpartiesSelection(cps));
      },

      hiddenLimitOrderToggle: (enabled: boolean) => {
        dispatch(hiddenLimitOrderToggle(enabled));
      },

      hiddenLimitOrderBidPriceChange: (price: string) => {
        dispatch(hiddenLimitOrderBidPriceChange(price));
      },

      hiddenLimitOrderAskPriceChange: (price: string) => {
        dispatch(hiddenLimitOrderAskPriceChange(price));
      },
    }),
    [defaultAccounts, dispatch, multiProductVenues],
  );
}
